import * as React from 'react';
import { apiError } from '@/lib/api-error';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageSquare, Users } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import AdvisorDirectory from '@/components/advisor/public/AdvisorDirectory';
import { DirectoryEntry } from '@/components/advisor/public/AdvisorCardGrid';
import AdvisorProfilePanel from '@/components/advisor/public/AdvisorProfilePanel';
import { type PublicOfferSummary } from '@/components/advisor/public/AdvisorProfileDetail';
import { useChat } from '@/context/ChatProvider';
import { useTitleBar } from '@/context/TitleBarProvider';
import { changeAdvisor } from '@/data/advisor-chat';
import { chooseAdvisor, fetchOnboardingState, requestPublicOffer } from '@/data/billing';
import { usePublicAdvisors } from '@/data/public-advisors';
import type { AdvisorSwitch } from '@/types/chat';

type Choice = { advisor: DirectoryEntry; switching: AdvisorSwitch | null; offer?: PublicOfferSummary; packageId?: string };

export default function FindAdvisorPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { openChat } = useChat();
  const { setTitle, setIcon, setButtons } = useTitleBar();
  const panelRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    setTitle('Find an automation advisor');
    setIcon(<Users size={24} />);
    setButtons(null);
  }, [setTitle, setIcon, setButtons]);

  const { data: advisors, isLoading } = usePublicAdvisors();
  const [chosen, setChosen] = React.useState<DirectoryEntry | null>(null);
  const [blockedBy, setBlockedBy] = React.useState<string | null>(null);

  const state = useQuery({
    queryKey: ['onboarding-state'],
    queryFn: () => fetchOnboardingState(),
    retry: false,
  });

  const current = state.data?.advisor ?? null;

  const pick = (advisor: DirectoryEntry) => {
    setChosen(advisor);
    setBlockedBy(null);
    if (window.matchMedia('(max-width: 1023px)').matches) {
      window.requestAnimationFrame(() => panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    }
  };

  const settle = () => {
    queryClient.invalidateQueries({ queryKey: ['chat-conversations'] });
    queryClient.invalidateQueries({ queryKey: ['chat-unread'] });
    queryClient.invalidateQueries({ queryKey: ['onboarding-state'] });
    queryClient.invalidateQueries({ queryKey: ['trial-status'] });
    queryClient.invalidateQueries({ queryKey: ['contracts'] });
  };

  const choose = useMutation({
    mutationFn: async ({ advisor, switching, offer, packageId }: Choice) => {
      let conversationId: string | undefined;
      if (current && current.id !== advisor.id) {
        const result = await changeAdvisor({ advisor_profile_id: advisor.id, ...(switching as AdvisorSwitch) });
        conversationId = result.conversation_id;
      } else if (!current) {
        await chooseAdvisor(advisor.id);
      }
      if (offer && packageId) await requestPublicOffer(offer.id, packageId);
      return { conversationId, offer };
    },
    onSuccess: ({ conversationId, offer }) => {
      settle();
      navigate(offer ? '/contracts' : '/dashboard');
      openChat(conversationId);
    },
    onError: (error) => {
      const body = (error as { response?: { data?: { code?: string } } }).response?.data;
      if (body?.code === 'running_contracts') {
        setBlockedBy(apiError(error, 'Cancel your running contract first.'));
        return;
      }
      toast.error(apiError(error, current ? 'Could not switch advisors.' : 'Could not link you to that advisor.'));
    },
  });

  return (
    <div className="w-full space-y-6 p-6" data-testid="find-advisor-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <p className="max-w-2xl text-muted-foreground">
          An automation advisor works out what is worth automating in your business,
          writes it down so it can be built, and sets the first ones up with you. Pick a
          card to read the full profile, then decide. You can change later.
        </p>
        {current && (
          <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 text-sm" data-testid="current-advisor">
            <span>
              <strong>{current.display_name}</strong> is your advisor today.
            </span>
            <Button variant="outline" size="sm" onClick={() => openChat()}>
              <MessageSquare className="mr-2 h-4 w-4" />
              Talk to them
            </Button>
          </div>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
        <div className="min-w-0">
          <AdvisorDirectory
            advisors={advisors ?? []}
            loading={isLoading}
            onSelect={pick}
            selectedId={chosen?.id ?? null}
            columns="single"
            emptyMessage="No advisors are accepting clients right now. Email support@bitswan.space and we will match you with one."
          />
        </div>
        <div ref={panelRef} className="min-w-0">
          {chosen ? (
            <AdvisorProfilePanel
              advisor={chosen}
              current={current}
              busy={choose.isPending}
              blockedBy={blockedBy}
              onChoose={(switching) => choose.mutate({ advisor: chosen, switching })}
              onRequest={(offer, packageId, switching) => choose.mutate({ advisor: chosen, switching, offer, packageId })}
              onClose={() => setChosen(null)}
              onTalk={() => openChat()}
            />
          ) : (
            <div
              className="hidden h-full min-h-[320px] flex-col items-center justify-center rounded-xl border border-dashed p-8 text-center text-muted-foreground lg:flex lg:sticky lg:top-6"
              data-testid="advisor-profile-empty"
            >
              <Users className="mb-3 h-8 w-8 text-amber-500/60" />
              <p className="max-w-xs text-sm">
                Pick an advisor on the left to read their whole profile here: who they are,
                what they automate, and where they work.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
